import { useState, useRef, useEffect, useCallback } from 'react';
import './style.scss';

interface DayRangeSelectProps {
    /** Callback khi giá trị thay đổi – trả về "2,12" hoặc [2,12] tùy returnType */
    onChange?: (value: string | [number, number]) => void;

    /**
     * Giá trị mặc định dạng "start,end" (ví dụ: "2,12")
     * - Nếu null / empty → mặc định "1,31"
     * - Nếu giá trị không nằm trong khoảng 1-31 → mặc định "1,31"
     */
    defaultValue?: string | null;

    /**
     * Kiểu giá trị trả về qua onChange
     * - 'string': trả về "2,12" (mặc định)
     * - 'array': trả về [2, 12]
     */
    returnType?: 'string' | 'array';

    /** Disabled toàn bộ component */
    disabled?: boolean;

    /** Placeholder cho ô bắt đầu */
    startPlaceholder?: string;

    /** Placeholder cho ô kết thúc */
    endPlaceholder?: string;
}

/**
 * Parse và validate defaultValue.
 * Trả về [start, end] hợp lệ trong khoảng 1-31.
 */
function parseDefaultValue(value?: string | null): [number, number] {
    const FALLBACK: [number, number] = [1, 31];

    if (!value || !value.trim()) return FALLBACK;

    const parts = value.split(',');

    // Trường hợp chỉ có 1 giá trị, ví dụ "20" → [1, 20]
    if (parts.length === 1) {
        const single = parseInt(parts[0], 10);
        if (Number.isNaN(single) || single < 1 || single > 31) return FALLBACK;
        return [1, single];
    }

    if (parts.length !== 2) return FALLBACK;

    const start = parseInt(parts[0], 10);
    const end = parseInt(parts[1], 10);

    if (Number.isNaN(start) || Number.isNaN(end)) return FALLBACK;
    if (start < 1 || start > 31 || end < 1 || end > 31) return FALLBACK;
    if (start > end) return FALLBACK;

    return [start, end];
}

const DAY_LIST: number[] = Array.from({ length: 31 }, (_, i) => i + 1);

type PickingPhase = 'idle' | 'start' | 'end';

function DayRangeSelect({
    onChange,
    defaultValue,
    returnType = 'string',
    disabled = false,
    startPlaceholder = 'Ngày BĐ',
    endPlaceholder = 'Ngày KT',
}: DayRangeSelectProps) {
    const [parsedStart, parsedEnd] = parseDefaultValue(defaultValue);
    const [startDay, setStartDay] = useState<number>(parsedStart);
    const [endDay, setEndDay] = useState<number>(parsedEnd);
    const [open, setOpen] = useState(false);
    const [phase, setPhase] = useState<PickingPhase>('idle');
    const [hoverDay, setHoverDay] = useState<number | null>(null);

    const containerRef = useRef<HTMLDivElement>(null);
    // Track whether any day was picked during the current open session
    const dirtyRef = useRef(false);

    const fireChange = useCallback(
        (start: number, end: number) => {
            if (onChange) {
                onChange(
                    returnType === 'array' ? [start, end] : `${start},${end}`,
                );
            }
        },
        [onChange, returnType],
    );

    // Click outside → đóng dropdown, commit partial selection nếu có
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (
                containerRef.current &&
                !containerRef.current.contains(e.target as Node)
            ) {
                if (dirtyRef.current) {
                    fireChange(startDay, endDay);
                    dirtyRef.current = false;
                }
                setOpen(false);
                setPhase('idle');
                setHoverDay(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () =>
            document.removeEventListener('mousedown', handleClickOutside);
    }, [startDay, endDay, fireChange]);

    const handleInputClick = (target: 'start' | 'end') => {
        if (disabled) return;
        setOpen(true);
        setPhase(target);
    };

    const handleDayClick = (day: number) => {
        dirtyRef.current = true;

        if (phase === 'start') {
            setStartDay(day);
            // Nếu day > endDay → đẩy endDay
            if (day > endDay) {
                setEndDay(day);
            }
            // Chuyển sang chọn ngày kết thúc
            setPhase('end');
        } else if (phase === 'end') {
            if (day < startDay) {
                // Nếu chọn nhỏ hơn start → đặt lại start, giữ phase end
                setStartDay(day);
                setPhase('end');
            } else {
                setEndDay(day);
                fireChange(startDay, day);
                dirtyRef.current = false;
                // Đóng dropdown
                setOpen(false);
                setPhase('idle');
                setHoverDay(null);
            }
        }
    };

    const isInRange = (day: number): boolean => {
        if (phase === 'end' && hoverDay !== null) {
            const effectiveEnd = hoverDay >= startDay ? hoverDay : startDay;
            const effectiveStart = hoverDay >= startDay ? startDay : hoverDay;
            return day >= effectiveStart && day <= effectiveEnd;
        }
        return day >= startDay && day <= endDay;
    };

    const isStart = (day: number): boolean => {
        if (phase === 'end' && hoverDay !== null && hoverDay < startDay) {
            return day === hoverDay;
        }
        return day === startDay;
    };

    const isEnd = (day: number): boolean => {
        if (phase === 'end' && hoverDay !== null) {
            return day === (hoverDay >= startDay ? hoverDay : startDay);
        }
        return day === endDay;
    };

    const getCellClassName = (day: number): string => {
        const classes = ['day-range-cell'];
        if (isStart(day)) classes.push('day-range-cell--start');
        if (isEnd(day)) classes.push('day-range-cell--end');
        if (isInRange(day)) classes.push('day-range-cell--in-range');
        return classes.join(' ');
    };

    return (
        <div
            className={`day-range-picker ${open ? 'day-range-picker--open' : ''} ${disabled ? 'day-range-picker--disabled' : ''}`}
            ref={containerRef}
        >
            {/* Input hiển thị */}
            <div className="day-range-picker__input">
                <span
                    role="button"
                    tabIndex={0}
                    className={`day-range-picker__value ${phase === 'start' ? 'day-range-picker__value--active' : ''}`}
                    onClick={() => handleInputClick('start')}
                    onKeyDown={(e) =>
                        e.key === 'Enter' && handleInputClick('start')
                    }
                >
                    {startDay ?? startPlaceholder}
                </span>
                <span className="day-range-picker__separator">~</span>
                <span
                    role="button"
                    tabIndex={0}
                    className={`day-range-picker__value ${phase === 'end' ? 'day-range-picker__value--active' : ''}`}
                    onClick={() => handleInputClick('end')}
                    onKeyDown={(e) =>
                        e.key === 'Enter' && handleInputClick('end')
                    }
                >
                    {endDay ?? endPlaceholder}
                </span>
                <span className="day-range-picker__icon">
                    <svg
                        viewBox="64 64 896 896"
                        width="14"
                        height="14"
                        fill="currentColor"
                    >
                        <path d="M880 184H712v-64c0-4.4-3.6-8-8-8h-56c-4.4 0-8 3.6-8 8v64H384v-64c0-4.4-3.6-8-8-8h-56c-4.4 0-8 3.6-8 8v64H144c-17.7 0-32 14.3-32 32v664c0 17.7 14.3 32 32 32h736c17.7 0 32-14.3 32-32V216c0-17.7-14.3-32-32-32zm-40 656H184V460h656v380zm0-448H184V256h128v48c0 4.4 3.6 8 8 8h56c4.4 0 8-3.6 8-8v-48h256v48c0 4.4 3.6 8 8 8h56c4.4 0 8-3.6 8-8v-48h128v136z" />
                    </svg>
                </span>
            </div>

            {/* Dropdown panel */}
            {open && (
                <div className="day-range-picker__dropdown">
                    <div className="day-range-picker__header">
                        Chọn ngày {phase === 'start' ? 'bắt đầu' : 'kết thúc'}
                    </div>
                    <div className="day-range-picker__grid">
                        {DAY_LIST.map((day) => (
                            <div
                                role="button"
                                tabIndex={0}
                                key={day}
                                className={getCellClassName(day)}
                                onClick={() => handleDayClick(day)}
                                onKeyDown={(e) =>
                                    e.key === 'Enter' && handleDayClick(day)
                                }
                                onMouseEnter={() => setHoverDay(day)}
                                onMouseLeave={() => setHoverDay(null)}
                            >
                                <span className="day-range-cell__inner">
                                    {day}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

export default DayRangeSelect;
